import {useGetEvents} from '@/features/agenda/queries/useGetEvents'
import {useAgendaCalendarStore} from '@/features/agenda/stores/agendaCalendarStore'
import {
    AgendaEventAbsence,
    AgendaEventAppointment,
    AgendaEventUnavailability,
    AgendaUrlParams
} from '@/features/agenda/types'
import {useAuthStore} from '@/features/auth/store/store'
import {useAvailabilitiesForMultipleTherapists} from '@/features/users-availability/hooks/useAvailabilitiesForMultipleTherapists'
import {UserAvailability} from '@/features/users-availability/types'
import {DEFAULT_END_TIME, DEFAULT_START_TIME} from '@/features/users-availability/utils'
import {ObjectKeys} from '@/utilities/helpers'
import dayjs from 'dayjs'
import {useMemo} from 'react'
import {useAgendaQueryParams} from '@/features/agenda/hooks/useAgendaQueryParams'
import {useAgendaSearchParams} from '@/features/agenda/hooks/useAgendaSearchParams'

type DisabledSlotsType = Record<
    string,
    {
        start: Date
        end: Date
        resourceId: string
    }[]
>

/**
 * Hook to get events from the agenda
 * @param date - The date to get events for
 * @param view - The view to get events for
 * @param entity - The entity to get events for
 * @returns The events and unavailabilities
 */
export const useAgendaEvents = ({date, view, entity}: AgendaUrlParams & {entityId?: string}) => {
    const currentUser = useAuthStore(state => state.user)
    const {
        searchParams: {hideDomiciliary}
    } = useAgendaSearchParams()

    // Get query params
    const params = useAgendaQueryParams({date, view, entity})

    const isQueryEnabled = useMemo(() => {
        return (
            !!currentUser?.id &&
            !!params.startDate &&
            !!params.endDate &&
            ((params.therapists && params.therapists?.length > 0) || (params.patients && params.patients?.length > 0))
        )
    }, [currentUser, params.startDate, params.endDate, params.therapists, params.patients])

    // Get events query
    const {data: eventsData, ...eventsQuery} = useGetEvents({
        params,
        options: {
            enabled: isQueryEnabled
        }
    })

    const statusFilter = useAgendaCalendarStore(state => state.statusFilter)

    // availabilities query for multiple therapists
    const {availabilitiesMap} = useAvailabilitiesForMultipleTherapists({
        therapistIds: params.therapists,
        enabled: !!(params.therapists && params.therapists.length > 0)
    })

    // Get all dates within the startDate and endDate range
    const datesInRange = useMemo(() => {
        if (!params.startDate || !params.endDate) return []

        const start = dayjs(params.startDate)
        const end = dayjs(params.endDate)
        const dates = []

        let current = start
        while (current.isBefore(end) || current.isSame(end, 'day')) {
            dates.push(current.toDate())
            current = current.add(1, 'day')
        }

        return dates
    }, [params.startDate, params.endDate])

    // Transform events to the AgendaEventType format
    const events = useMemo(() => {
        return eventsData?.map(event => {
            let startDate = dayjs(event.date)
            startDate =
                event.time && event.time != '00:00:00'
                    ? startDate
                          .set('hour', parseInt(event.time.split(':')[0]))
                          .set('minute', parseInt(event.time.split(':')[1]))
                    : startDate.set('hour', 8).set('minute', 0)
            const endDate = startDate.add(event.duration ?? 0, 'minutes')

            if (event.type == 'event' || event.type == 'generic_event') {
                return {
                    ...event,
                    start: startDate.toDate(),
                    end: endDate.toDate(),
                    isDraggable: true
                }
            }

            return {
                ...event,
                start: startDate.toDate(),
                end: endDate.toDate(),
                isDraggable: false
            }
        })
    }, [eventsData])

    const appointments = useMemo(() => {
        let appointments = []
        for (const event of events || []) {
            if (event.type !== 'event' && event.type !== 'generic_event') continue

            const eventUsers = [
                ...new Set([
                    ...(event.eventConsultants?.map(c => c.userId) || []),
                    ...(event.eventConsultants?.map(c => c.substituteId).filter((id): id is string => !!id) || []),
                    ...(event.eventTeams?.map(t => t.member.userId) || []),
                    ...(event.eventPatients?.map(p => p.patientId) || [])
                ])
            ]

            if (entity == 'users') {
                for (const userId of eventUsers) {
                    if (!params.therapists?.includes(userId)) continue

                    appointments.push({
                        ...event,
                        resourceId: userId
                    })
                }
            }

            if (entity == 'patients') {
                for (const patientId of event.eventPatients?.map(p => p.patientId) || []) {
                    if (!params.patients?.includes(patientId)) continue
                    appointments.push({
                        ...event
                    })
                }
            }
        }

        appointments = hideDomiciliary
            ? appointments.filter(appointment => appointment.sector != 'domiciliary')
            : appointments

        return appointments.filter(appointment => {
            if (!statusFilter) return true
            return appointment.status == statusFilter
        }) as AgendaEventAppointment[]
    }, [events, params.therapists, params.patients, entity, statusFilter, hideDomiciliary])

    const absences = useMemo((): AgendaEventAbsence[] => {
        const absences = []

        for (const event of events || []) {
            if (event.type === 'event' || event.type === 'generic_event') continue

            const eventUsers = [
                ...new Set([
                    ...(event.eventConsultants?.map(c => c.userId) || []),
                    ...(event.eventTeams?.map(t => t.member.userId) || []),
                    ...(event.eventPatients?.map(p => p.patientId) || [])
                ])
            ]

            if (entity == 'users') {
                for (const userId of eventUsers) {
                    if (!params.therapists?.includes(userId)) continue
                    const start = event.time
                        ? dayjs(event.date)
                              .set('hour', parseInt(event.time.split(':')[0]))
                              .set('minute', parseInt(event.time.split(':')[1]))
                              .toDate()
                        : dayjs(event.date).startOf('day').toDate()

                    const end = event.duration
                        ? dayjs(start).add(event.duration, 'minutes').toDate()
                        : dayjs(start).endOf('day').toDate()

                    absences.push({
                        ...event,
                        start,
                        end,
                        resourceId: userId
                    })
                }
            }

            if (entity == 'patients') {
                for (const patientId of event.eventPatients?.map(p => p.patientId) || []) {
                    if (!params.patients?.includes(patientId)) continue
                    const start = event.time
                        ? dayjs(event.date)
                              .set('hour', parseInt(event.time.split(':')[0]))
                              .set('minute', parseInt(event.time.split(':')[1]))
                              .toDate()
                        : dayjs(event.date).startOf('day').toDate()

                    const end = event.duration
                        ? dayjs(start).add(event.duration, 'minutes').toDate()
                        : dayjs(start).endOf('day').toDate()

                    absences.push({
                        ...event,
                        start,
                        end
                    })
                }
            }
        }

        return absences as AgendaEventAbsence[]
    }, [events, params.therapists, params.patients, entity])

    // Generate unavailability slots based on user availability settings and absences
    // These slots values exclude the absences to disallow the overlap of two different events in the same day
    const unavailabilitiesExcludedAbsences = useMemo(() => {
        if (!availabilitiesMap || ObjectKeys(availabilitiesMap).length === 0 || !datesInRange.length) return {}

        return datesInRange.reduce<DisabledSlotsType>((acc, date) => {
            const dateKey = dayjs(date).format('YYYY-MM-DD')

            // Process each therapist's availability
            for (const therapistId of params.therapists || []) {
                const availability = availabilitiesMap[therapistId]
                if (!availability) continue

                const dayOfWeek = dayjs(date).format('dddd').toLowerCase() as keyof UserAvailability

                // Extract day's availability from the user availability settings
                const dayAvailability = availability[dayOfWeek]

                // If day is disabled or has no time slots, mark entire day as unavailable
                if (!dayAvailability?.enabled || dayAvailability?.timeSlots.length === 0) {
                    if (!acc[dateKey]) acc[dateKey] = []
                    acc[dateKey].push({
                        start: dayjs(date).hour(8).minute(0).toDate(),
                        end: dayjs(date).hour(19).minute(0).toDate(),
                        resourceId: therapistId
                    })
                    continue
                }

                // Sort available time slots by start time
                const sortedTimeSlots = [...dayAvailability.timeSlots].sort((a, b) => a.start.localeCompare(b.start))

                // Find gaps between available slots (these are the unavailable periods)
                const unavailableSlots: Array<{start: string; end: string}> = []

                // Check if we need to add unavailable slot before first available slot
                if (sortedTimeSlots[0].start > DEFAULT_START_TIME) {
                    unavailableSlots.push({
                        start: DEFAULT_START_TIME,
                        end: sortedTimeSlots[0].start
                    })
                }

                // Add unavailable slots between available slots
                for (let i = 0; i < sortedTimeSlots.length - 1; i++) {
                    if (sortedTimeSlots[i].end < sortedTimeSlots[i + 1].start) {
                        unavailableSlots.push({
                            start: sortedTimeSlots[i].end,
                            end: sortedTimeSlots[i + 1].start
                        })
                    }
                }

                // Check if we need to add unavailable slot after last available slot
                const lastSlot = sortedTimeSlots[sortedTimeSlots.length - 1]
                if (lastSlot.end < DEFAULT_END_TIME) {
                    unavailableSlots.push({
                        start: lastSlot.end,
                        end: DEFAULT_END_TIME
                    })
                }

                // Convert time strings to dayjs Date objects - no timezone concerns
                const formattedUnavailableSlots = unavailableSlots.map(slot => {
                    const [startHour, startMinute] = slot.start.split(':').map(Number)
                    const [endHour, endMinute] = slot.end.split(':').map(Number)

                    return {
                        start: dayjs(date).hour(startHour).minute(startMinute).second(0).millisecond(0).toDate(),
                        end: dayjs(date).hour(endHour).minute(endMinute).second(0).millisecond(0).toDate(),
                        resourceId: therapistId
                    }
                })

                // Calculate unavailable slots excluding parts that overlap with absence slots
                const absencesForDateAndTherapist =
                    absences?.filter(
                        absence =>
                            dayjs(absence.start).format('YYYY-MM-DD') === dateKey && absence.resourceId === therapistId
                    ) || []

                const filteredSlots = formattedUnavailableSlots.reduce<
                    Array<{start: Date; end: Date; resourceId: string}>
                >((acc, unavailableSlot) => {
                    // Start with the original slot
                    let remainingSlots = [unavailableSlot]

                    // For each absence, subtract it from all remaining slots
                    for (const absence of absencesForDateAndTherapist) {
                        const absenceStart = dayjs(absence.start)
                        const absenceEnd = dayjs(absence.end)

                        const newRemainingSlots: Array<{start: Date; end: Date; resourceId: string}> = []

                        for (const slot of remainingSlots) {
                            const slotStart = dayjs(slot.start)
                            const slotEnd = dayjs(slot.end)

                            // Check if absence overlaps with this slot
                            const hasOverlap =
                                (slotStart.isBefore(absenceEnd) || slotStart.isSame(absenceEnd)) &&
                                (slotEnd.isAfter(absenceStart) || slotEnd.isSame(absenceStart))

                            if (!hasOverlap) {
                                // No overlap, keep the slot as is
                                newRemainingSlots.push(slot)
                            } else {
                                // There's overlap, need to split the slot
                                // Add part before absence (if any)
                                if (slotStart.isBefore(absenceStart)) {
                                    newRemainingSlots.push({
                                        start: slot.start,
                                        end: absenceStart.toDate(),
                                        resourceId: slot.resourceId
                                    })
                                }

                                // Add part after absence (if any)
                                if (slotEnd.isAfter(absenceEnd)) {
                                    newRemainingSlots.push({
                                        start: absenceEnd.toDate(),
                                        end: slot.end,
                                        resourceId: slot.resourceId
                                    })
                                }
                            }
                        }

                        remainingSlots = newRemainingSlots
                    }

                    acc.push(...remainingSlots)
                    return acc
                }, [])

                if (!acc[dateKey]) acc[dateKey] = []
                acc[dateKey].push(...filteredSlots)
            }

            return acc
        }, {})
    }, [availabilitiesMap, datesInRange, absences, params.therapists])

    const unavailabilitiesEvents = useMemo(() => {
        if (!unavailabilitiesExcludedAbsences || ObjectKeys(unavailabilitiesExcludedAbsences).length == 0) return []

        return ObjectKeys(unavailabilitiesExcludedAbsences).flatMap(dateKey => {
            return unavailabilitiesExcludedAbsences[dateKey]
                .map((slot, index) => {
                    // Create base event properties to match the AgendaEventType shape
                    const baseEvent = {
                        id: `unavailable-${dateKey}-${slot.resourceId}-${index}`,
                        start: slot.start,
                        end: slot.end,
                        isDraggable: false,
                        type: 'unavailability',
                        resourceId: slot.resourceId
                    }

                    return baseEvent
                })
                .filter(unavailability => {
                    // Check if this unavailability overlaps with any absence for the same therapist and date
                    const absencesForDateAndTherapist =
                        absences?.filter(
                            absence =>
                                dayjs(absence.start).format('YYYY-MM-DD') === dateKey &&
                                absence.resourceId === unavailability.resourceId
                        ) || []

                    // If there are no absences for this therapist on this date, keep the unavailability
                    if (absencesForDateAndTherapist.length === 0) {
                        return true
                    }

                    // Check if this unavailability is completely covered by any absence
                    const isCompletelyCovered = absencesForDateAndTherapist.some(absence => {
                        const absenceStart = dayjs(absence.start)
                        const absenceEnd = dayjs(absence.end)
                        const unavailabilityStart = dayjs(unavailability.start)
                        const unavailabilityEnd = dayjs(unavailability.end)

                        // Check if the absence completely covers the unavailability
                        return (
                            (absenceStart.isBefore(unavailabilityStart) || absenceStart.isSame(unavailabilityStart)) &&
                            (absenceEnd.isAfter(unavailabilityEnd) || absenceEnd.isSame(unavailabilityEnd))
                        )
                    })

                    // If the unavailability is completely covered by an absence, filter it out
                    return !isCompletelyCovered
                })
        }) as AgendaEventUnavailability[]
    }, [unavailabilitiesExcludedAbsences, absences])

    return {
        ...eventsQuery,
        params,
        events,
        startDate: params.startDate,
        endDate: params.endDate,
        appointments,
        blockouts: absences,
        unavailabilities: unavailabilitiesEvents
    }
}
